'use client';

import { useEffect, useId } from 'react';

interface ConfirmacaoDigitadaProps {
  /** Texto que o usuário precisa digitar para confirmar (ex.: "C-2026-000123"). */
  expectedText: string;
  /** Label acima do input. */
  label?: string;
  /** Label do valor esperado (ex.: "Código:"). */
  hintLabel?: string;
  /** Default: true. */
  caseSensitive?: boolean;
  /** Default: true (ignora espaços nas pontas). */
  trim?: boolean;
  value: string;
  onValueChange: (v: string) => void;
  /** Disparado a cada mudança de validade — usuário desabilita botão com base nisso. */
  onValidChange: (isValid: boolean) => void;
  className?: string;
}

/**
 * Input de confirmação por digitação para ações destrutivas/críticas.
 *
 * Cont-4c (e Cont-5c, Cont-6c): user precisa digitar valor exato para
 * habilitar botão de ação (inativar, desbloquear, reprocessar, etc.).
 *
 * Borda do input muda de cor: cinza (vazio) → amarelo (parcial) →
 * verde (bate) → vermelho (digitou algo mas não bate).
 *
 * @example
 * const [v, setV] = useState('');
 * const [valido, setValido] = useState(false);
 * <ConfirmacaoDigitada
 *   expectedText="C-2026-000123"
 *   label="Para confirmar, digite o código:"
 *   value={v}
 *   onValueChange={setV}
 *   onValidChange={setValido}
 * />
 * <button disabled={!valido}>Confirmar</button>
 */
export default function ConfirmacaoDigitada({
  expectedText,
  label = 'Para confirmar, digite o valor abaixo:',
  hintLabel,
  caseSensitive = true,
  trim = true,
  value,
  onValueChange,
  onValidChange,
  className = '',
}: ConfirmacaoDigitadaProps) {
  const inputId = useId();
  const hintId = useId();

  // Avalia validade
  const normalize = (s: string) => {
    const v = trim ? s.trim() : s;
    return caseSensitive ? v : v.toLowerCase();
  };
  const target = normalize(expectedText);
  const current = normalize(value);
  const isValid = current === target && current.length > 0;
  const isPartial = current.length > 0 && !isValid && target.startsWith(current);
  const isWrong = current.length > 0 && !isValid && !isPartial;

  // Reporta validade para o pai
  useEffect(() => {
    onValidChange(isValid);
  }, [isValid, onValidChange]);

  const borderClass = isValid
    ? 'border-[var(--success-500)] focus:ring-[var(--success-500)]'
    : isWrong
      ? 'border-[var(--error-500)] focus:ring-[var(--error-500)]'
      : isPartial
        ? 'border-[var(--warning-500)] focus:ring-[var(--warning-500)]'
        : 'border-[var(--gray-300)] focus:ring-[var(--brand-500)]';

  return (
    <div className={`space-y-2 ${className}`}>
      <label htmlFor={inputId} className="block text-sm font-medium text-[var(--gray-700)]">
        {label}
      </label>
      {hintLabel && (
        <p id={hintId} className="text-sm text-[var(--gray-600)]">
          {hintLabel}{' '}
          <code className="rounded bg-[var(--gray-100)] px-1.5 py-0.5 font-mono text-xs">
            {expectedText}
          </code>
        </p>
      )}
      <input
        id={inputId}
        type="text"
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        aria-describedby={hintLabel ? hintId : undefined}
        aria-invalid={isWrong}
        autoComplete="off"
        spellCheck={false}
        className={`w-full rounded-md border px-3 py-2 font-mono text-sm shadow-sm focus:outline-none focus:ring-2 ${borderClass}`}
      />
      {isWrong && (
        <p className="text-xs text-[var(--error-700)]">
          <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />O valor digitado
          não confere.
        </p>
      )}
      {isValid && (
        <p className="text-xs text-[var(--success-700)]">
          <i className="pi pi-check-circle mr-1" aria-hidden="true" />Valor confirmado.
        </p>
      )}
    </div>
  );
}
